import { useState } from 'react'
import { cover } from '../../utility/constant/helper.constant'
import { Subject, WidthObserver, DepthObserver } from '../../classes/observer'
import {
  TPoles,
  TTerraceDepth,
  TTerraceModel,
  TTerraceWidth,
} from '../../utility/type/terraceCover.type'

/**
 * Values and methods are used by the context API
 * @returns states being used to manipulate the scene
 */
export const useStateMeasure = () => {
  /**
   * Inititialize subjces and observer classes
   */
  const subject = new Subject()
  const width = new WidthObserver(subject)
  const depth = new DepthObserver(subject)

  /**
   * States that are manipulated by user input
   */
  const [userWidth, setUserWidth] = useState<TTerraceWidth>(
    cover.terraceCover.width
  )
  const [userDepth, setUserDepth] = useState<TTerraceDepth>(
    cover.terraceCover.depth
  )
  const [userAngle, setUserAngle] = useState(cover.bar.angle)

  const [userHeight, setUserHeight] = useState(cover.terraceCover.height)

  const [userPoles, setUserPoles] = useState<TPoles>(
    cover.terraceCover.poles
  )

  /**
   * States that are dependend on userDepth
   */
  const [terraceDepth, setTerraceDepth] = useState(cover.terrace.depth)

  const [angleCompensation, setAngleCompensation] = useState(
    cover.terraceCover.angleCompensation()
  )

  /**
   * States that are dependend on userWidth
   */
  const [userModel, setUserModel] = useState(cover.terraceCover.model)

  const [windowsTopWidth, setWindowsTopWidth] = useState(
    cover.terraceCover.windowsTopWidth()
  )

  const [windowsFrontWidth, setWindowsFrontWidth] = useState(
    cover.terraceCover.windowsFrontWidth()
  )

  const [windowsTopTotal, setWindowsTopTotal] = useState(
    cover.terraceCover.windowsTopTotal
  )

  const [windowsFrontTotal, setWindowsFrontTotal] = useState(
    cover.terraceCover.windowsFrontTotal
  )

  const [logsTopTotal, setLogsTopTotal] = useState(
    cover.terraceCover.logsTopTotal()
  )

  const [logsFrontTotal, setLogsFrontTotal] = useState(
    cover.terraceCover.logsFrontTotal()
  )

  /**
   * States that are dependend on userDepth
   */
  const [windowsSideTotal, setWindowsSideTotal] = useState(
    cover.terraceCover.windowsSideTotal
  )
  const [windowsSideWidth, setwindowsSideWidth] = useState(
    cover.terraceCover.windowsSideWidth()
  )
  const [logsSideTotal, setLogsSideTotal] = useState(
    cover.terraceCover.logsSideTotal()
  )

  /** States that are dependend on multiple other states
   * @description Dependend on depth, angle, height
   */
  const [frontHeight, setFrontHeight] = useState(
    cover.terraceCover.frontHeight()
  )

  /**
   * Enable the user to change the width
   */
  const onHandleWidth = (value: TTerraceWidth) => {
    subject.setWidth(+value as TTerraceWidth).setModel(userModel)

    setUserWidth(+value as TTerraceWidth)
    setWindowsTopTotal(width.windowsTopTotal)
    setWindowsFrontTotal(width.windowsFrontTotal)
    setWindowsTopWidth(width.windowsTopWidth)
    setWindowsFrontWidth(width.windowsFrontWidth)
    setLogsTopTotal(width.logsTopTotal)

    setLogsFrontTotal(width.logsFrontTotal)
  }

  /**
   * Enable the user to change the depth
   */
  const onHandleDepth = (value: TTerraceDepth) => {
    subject
      .setDepth(+value as TTerraceDepth)
      .setAngle(userAngle)
      .setHeight(userHeight)

    setUserDepth(+value as TTerraceDepth)
    setAngleCompensation(depth.angleCompensation)
    setFrontHeight(depth.frontHeight)
    setTerraceDepth(depth.terraceDepth)
    setWindowsSideTotal(depth.windowsSideTotal)
    setwindowsSideWidth(depth.windowsSideWidth)
    setLogsSideTotal(depth.logsSideTotal)
  }

  /**
   * Enable the user to change the angle
   */
  const onHandleAngle = (value: number) => {
    subject.setDepth(userDepth).setAngle(value).setHeight(userHeight)

    setUserAngle(+value)
    setAngleCompensation(depth.angleCompensation)
    setFrontHeight(depth.frontHeight)
  }

  /**
   * Enable the user to change the height
   */
  const onHandleHeight = (value: number) => {
    subject
      .setDepth(userDepth)
      .setAngle(userAngle)
      .setHeight(+value)

    setUserHeight(+value)
    setFrontHeight(depth.frontHeight)
  }

  const onHandleModel = (value: TTerraceModel) => {
    subject.setWidth(userWidth).setModel(value)

    setUserModel(value)
    setWindowsTopTotal(width.windowsTopTotal)
    setWindowsTopWidth(width.windowsTopWidth)
  }

  const onHandlePoles = (value: TPoles) => {
    setUserPoles(value)
  }

  return {
    measure: {
      userModel,
      userWidth,
      userDepth,
      userAngle,
      userHeight,
      terraceDepth,
      windowsTopTotal,
      windowsTopWidth,
      logsTopTotal,
      windowsFrontTotal,
      windowsFrontWidth,
      logsFrontTotal,
      windowsSideTotal,
      windowsSideWidth,
      logsSideTotal,
      frontHeight,
      angleCompensation,
      userPoles,
    },

    measureMethod: {
      onHandleWidth,
      onHandleDepth,
      onHandleAngle,
      onHandleHeight,
      onHandleModel,
      onHandlePoles,
    },
  }
}
